'use client';

import { useEffect, useRef, useState } from 'react';
import { PageHeader, StateKit } from '@statura/ui';
import { Banner } from '@statura/ui/src/components/detail-page/banner';
import { ProgramLensActionButton } from '@/components/nav-v2/sidebar/program-lens-action-button';
import { programFamilyLabel } from '@/lib/navigation/provider-type';
import type { ProgramNavigationHint } from '@/lib/experience/program-navigation-hint';
import { recordProgramNavigationHintRender } from '@/lib/experience/program-navigation-hint-telemetry';

export function ProgramNavigationHintState({
  hint,
  returnTo,
  currentFamily,
  standalone = false,
}: {
  hint: ProgramNavigationHint;
  returnTo: string;
  currentFamily?: string | null;
  standalone?: boolean;
}) {
  const recorded = useRef(false);
  const container = useRef<HTMLElement | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (recorded.current || hint.kind === 'unavailable' || !hint.targets.length) return;
    const heading = container.current?.querySelector('h1');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    recorded.current = true;
    recordProgramNavigationHintRender(hint);
  }, [hint]);
  if (hint.kind === 'unavailable' || !hint.targets.length) return null;
  const medications = /\/medications(?:\/|[?#]|$)/u.test(returnTo);
  const title = medications
    ? 'Choose a programme to view medications'
    : 'Choose a programme to open this client record';
  const label = hint.targets.length === 1 ? programFamilyLabel(hint.targets[0]!.family) : '';
  const description =
    hint.targets.length > 1
      ? hint.kind === 'medications-programme'
        ? `Medication orders are also available under ${hint.targets.map((target) => programFamilyLabel(target.family)).join(' and ')}. Choose a programme to continue.`
        : 'This client is enrolled in more than one programme. Choose a programme to continue.'
      : hint.kind === 'medications-programme'
        ? `This client's medications are under ${label}.`
        : `This client is enrolled in ${label}.`;
  const Container = standalone ? 'main' : 'div';
  return (
    <Container
      ref={(element: HTMLElement | null) => {
        container.current = element;
      }}
      className="mx-auto w-full min-w-0 max-w-xl space-y-6 px-4 py-10"
    >
      <PageHeader title={title} />
      <StateKit
        state="empty"
        title={description}
        actions={
          <div
            className="w-full min-w-0 space-y-2"
            role="group"
            aria-label="Choose a programme workspace"
            aria-busy={pending !== null || undefined}
          >
            {failed ? (
              <Banner tone="danger" role="alert" className="text-text-body">
                Couldn't switch programme. Try again.
              </Banner>
            ) : null}
            <p className="sr-only" role="status">
              {pending ? `Switching to ${pending}…` : ''}
            </p>
            <div className="grid min-w-0 gap-2 lg:grid-cols-2">
              {hint.targets.map((target) => {
                const targetLabel = programFamilyLabel(target.family);
                return (
                  <ProgramLensActionButton
                    key={target.family}
                    returnTo={returnTo}
                    {...(target.selection.kind === 'workspace'
                      ? { workspace: target.selection.value }
                      : { programFamily: target.selection.value })}
                    disabled={pending !== null || currentFamily === target.family}
                    pendingLabel={`Switching to ${targetLabel}…`}
                    onPendingChange={(busy) => {
                      setPending(busy ? targetLabel : null);
                      if (busy) setFailed(false);
                    }}
                    onSwitchError={() => setFailed(true)}
                    className="w-full min-w-0 rounded-control border border-control-border/30 bg-surface-sheet px-3 py-2.5 text-left text-body font-medium text-text-body hover:bg-surface-canvas focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-70 whitespace-normal"
                  >
                    {currentFamily === target.family
                      ? `${targetLabel} (current)`
                      : `Switch to ${targetLabel}`}
                  </ProgramLensActionButton>
                );
              })}
            </div>
          </div>
        }
      />
    </Container>
  );
}
